import React, { useContext, useEffect, useMemo } from 'react';
import classes from './PostCardGrid.module.scss';
import { PostCard } from '../PostCard';
import { InstagramApiType, SocialProfileFieldsFragment } from '../../../../models/graphql';
import { FindContentContext } from '../FindContentProvider';
import Masonry from '@mui/lab/Masonry';
import { UseFormReturn } from 'react-hook-form';
import { Loader } from '../../../../components/containers/Loader';
import { FloatingActionBoxModeEnum } from '../../../../components/containers/FloatingActionBar/FloatingActionBar.view';
import { Button } from '../../../../components/actions/Button';
import { ButtonColorsEnum } from '../../../../constants/button-colors';
import { FloatingActionBar } from '../../../../components/containers/FloatingActionBar';
import { InitialPostEditorState } from '../../../PostEditor/states/initial';
import { AppContext } from '../../../../providers/AppProvider';
import { MediaTypePostEditorState, PostEditorContextEnum } from '../../../../models/post-editor';
import {
  getMediaFromThirdPartyPost,
  mapThirdPartyPostToState
} from '../../../PostEditor/helpers/mapThirdPartyPostToState';
import updateImmutable from 'immutability-helper';
import { PostEditorMediaAddFromExternalDeferredAction } from '../../../PostEditor/states/PostEditorActions';
import { showToast } from '../../../../helpers/showToast';
import { GENERIC_ERROR_MESSAGE } from '../../../../constants/apollo-errors';
import { usePostLinkSettings } from '../../../../hooks/usePostLinkSettings';
import { triggerErrorToast } from '../../../../helpers/triggerErrorToast';
import { getPostSourceFrom3rdPartySource } from '../../../PostEditor/helpers/setPostSource';
import { BoxNotification } from 'components/containers/BoxNotification';
import { Link } from 'react-router-dom';
import { useOrganizationPlan } from 'hooks/useOrganizationPlan';
import { useSubscriptionStatus } from 'hooks/useSubscriptionStatus';

export type PostCardGridViewProps = {
  parentWidth: number;
  profiles: SocialProfileFieldsFragment[];
  engageAsHandler: (id: string) => void;
  formInstance: UseFormReturn;
};

export const PostCardGridView = (props: PostCardGridViewProps): JSX.Element => {
  const { parentWidth } = props;

  const {
    posts,
    loading,
    loadingMore,
    selectedPosts,
    setSelectedPosts,
    sourceType,
    gridRef,
    platform,
    platformType,
    selectedProfileSupportedMetrics,
    profileUsedToSearch
  } = useContext(FindContentContext);
  const { setPostEditorContext, selectedWorkspaceId, setLimitReachedModalState, setSuspendedModalOpen } = useContext(AppContext);
  const columnCount = useMemo(() => (parentWidth ? Math.floor(parentWidth / 320) : 4), [parentWidth]);
  const columnBreakPoints = useMemo(() => (columnCount ? columnCount : 4), [columnCount]);
  const mediaItemCount = useMemo(() => posts.length, [posts]);
  const { canImportFromNetwork, canAddPosts } = useOrganizationPlan();
  const { isTemporallySuspended } = useSubscriptionStatus();

  const postCards = useMemo(
    () =>
      posts.map((post, index) => {
        return (
          <PostCard
            key={`PostCard-${index}`}
            post={post}
            selectedPosts={selectedPosts}
            setSelectedPosts={setSelectedPosts}
            sourceType={sourceType}
            availableMetrics={selectedProfileSupportedMetrics}
            selectedProfile={profileUsedToSearch}
          />
        );
      }),
    [posts, selectedPosts, setSelectedPosts, sourceType, selectedProfileSupportedMetrics, profileUsedToSearch]
  );

  const { loading: linkSettingsLoading, error: linkSettingsError, ...restLinkSettings } = usePostLinkSettings();

  useEffect(() => {
    if (linkSettingsError) {
      triggerErrorToast('Error getting link settings')(linkSettingsError);
    }
  }, [linkSettingsError]);

  return (
    <div ref={gridRef}>
      {loading ? (
        <div className={classes.LoaderWrapper}>
          <Loader variation={'dark'} size={'large'} type={'inline'} />
        </div>
      ) : (
        <div className={classes.PostCardGrid}>
          {profileUsedToSearch?.__typename === 'InstagramSocialProfile' &&
            posts.length > 0 &&
            profileUsedToSearch.apiType === InstagramApiType.BASIC_DISPLAY && (
              <div className={classes.BoxInfo}>
                <BoxNotification type={'warning'}>
                  To see engagement stats for these posts, and sort posts by most engagement, you need to{' '}
                  <Link to={'/settings/profiles'} target={'_blank'}>
                    connect your instagram profile via a Facebook page.
                  </Link>
                </BoxNotification>
              </div>
            )}
          {postCards.length > 0 && (
            <Masonry columns={columnBreakPoints < mediaItemCount ? columnBreakPoints : mediaItemCount} spacing={2}>
              {postCards}
            </Masonry>
          )}
          {loadingMore && <Loader variation={'dark'} size={'small'} type={'inline'} />}
        </div>
      )}
      {selectedPosts.length > 0 && (
        <div className={classes.FloatingActionBarWrapper}>
          <FloatingActionBar
            mode={FloatingActionBoxModeEnum.DARK}
            selectedLabel={'Posts selected'}
            itemsSelectedCount={selectedPosts.length}>
            <Button
              type={'button'}
              variant={'hollow'}
              size={'medium'}
              loading={linkSettingsLoading}
              color={ButtonColorsEnum.white}
              action={() => {
                if (!canImportFromNetwork()) {
                  setLimitReachedModalState({
                    type: 'basicImport',
                    isOpen: true
                  });
                  return;
                }
                if (!canAddPosts(1)){
                  setLimitReachedModalState({
                    type: 'posts',
                    isOpen: true
                  });
                  return;
                }
                if (isTemporallySuspended) {
                  setSuspendedModalOpen(true);
                  return;
                }
                // ...
                if (!selectedWorkspaceId) {
                  showToast({
                    severity: 'error',
                    detail: GENERIC_ERROR_MESSAGE,
                    summary: 'Error'
                  });
                  return;
                }
                const mappedPosts = posts.filter(post => selectedPosts.map(sp => sp.id).includes(post.externalId));
                const source = !!platform && getPostSourceFrom3rdPartySource(platform, platformType, true);
                if (!source) return;
                let initialState = InitialPostEditorState();
                const externalMedia: MediaTypePostEditorState[][] = mappedPosts.map(post =>
                  getMediaFromThirdPartyPost(post)
                );

                initialState = updateImmutable(initialState, {
                  postGroups: {
                    $set: mapThirdPartyPostToState(mappedPosts, restLinkSettings, source)
                  }
                });
                const payload: PostEditorMediaAddFromExternalDeferredAction['payload'] = {
                  externalMedia
                };

                setPostEditorContext({
                  type: PostEditorContextEnum.import,
                  initialState,
                  payload: { type: 'mediaAddFromExternalDeferred', payload }
                });
              }}>
              Bulk Import Selected
            </Button>
          </FloatingActionBar>
        </div>
      )}
    </div>
  );
};
